import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { FC, useCallback, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { stylesheet } from "../stylesheet";
import { Color } from "../colors";
import { AssetSelectionDataGrid } from "./AssetSelectionDataGrid";
import { AttachmentResponse, WebAssetResponse } from "@akitabox/api-client";

// templates
import { imageGrid } from "./templates/imageGrid";
import { singleImage } from "./templates/singleImage";
import { imageGridWithDetails } from "./templates/imageGridWithDetails";
import { singleImageWithDetails } from "./templates/singleImageWithDetails";

export const ASSET_SELECTION_TEMPLATE_OPTIONS = [
  "image-grid-with-details",
  "image-grid",
  "single-image-with-details",
  "single-image",
] as const;

export type AssetSelectionTemplate =
  typeof ASSET_SELECTION_TEMPLATE_OPTIONS[number];

export type AssetAttachmentSelection = {
  [key: string]: {
    default?: AttachmentResponse;
    attachments: AttachmentResponse[];
    loading: boolean;
  };
};

export type AssetSelectionDialogResult =
  | {
      action: "cancel";
    }
  | {
      action: "confirm";
      assets: WebAssetResponse[];
      attachments: AssetAttachmentSelection;
      template: AssetSelectionTemplate;
    };

export interface AssetSelectionDialogProps
  extends Omit<DialogProps, "onClose"> {
  organizationId: string;
  onClose: (result: AssetSelectionDialogResult) => Promise<void>;
}

export const AssetSelectionDialog: FC<AssetSelectionDialogProps> = ({
  organizationId,
  onClose,
  ...props
}) => {
  const [loading, setLoading] = useState(false);
  const [canSelectImages, setCanSelectImages] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<WebAssetResponse[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<AssetSelectionTemplate>("image-grid-with-details");
  const [selectedAttachments, setSelectedAttachments] =
    useState<AssetAttachmentSelection>({});

  const cancelDialog = useCallback(
    () => onClose({ action: "cancel" }),
    [onClose]
  );

  const handleClose = useCallback<
    Exclude<DialogProps["onClose"], null | undefined>
  >(
    (e, reason) => {
      if (reason === "backdropClick" || loading) {
        return;
      }
      cancelDialog();
    },
    [cancelDialog, loading]
  );

  useEffect(() => {
    if (
      selectedTemplate === ASSET_SELECTION_TEMPLATE_OPTIONS[0] ||
      selectedTemplate === ASSET_SELECTION_TEMPLATE_OPTIONS[1]
    ) {
      setCanSelectImages(false);
    } else {
      setCanSelectImages(true);
    }
  }, [selectedTemplate]);

  const isAttachmentsLoading = () => {
    return (
      Object.keys(selectedAttachments).findIndex(
        (key) => selectedAttachments[key].loading
      ) >= 0
    );
  };

  return (
    <Dialog fullWidth maxWidth="md" {...props} onClose={handleClose}>
      <DialogTitle component={Box} css={ss.dialogTitle}>
        <Typography variant="h6" color="inherit">
          Insert Asset Data
        </Typography>
        <IconButton color="inherit" onClick={cancelDialog}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <AssetSelectionDataGrid
          organizationId={organizationId}
          selectedTemplate={selectedTemplate}
          setSelectedAssets={setSelectedAssets}
          setSelectedTemplate={setSelectedTemplate}
          selectedAttachments={selectedAttachments}
          setSelectedAttachments={setSelectedAttachments}
          canSelectImages={canSelectImages}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={cancelDialog} disabled={loading}>
          CANCEL
        </Button>
        <LoadingButton
          loading={loading || isAttachmentsLoading()}
          variant="contained"
          color="primary"
          disabled={
            selectedAssets.length === 0 || loading || isAttachmentsLoading()
          }
          onClick={async () => {
            setLoading(true);
            try {
              await onClose({
                action: "confirm",
                assets: [...selectedAssets],
                attachments: selectedAttachments,
                template: selectedTemplate,
              });
            } finally {
              setLoading(false);
            }
          }}
        >
          INSERT
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

const ss = stylesheet({
  dialogTitle: {
    backgroundColor: Color.MidnightExpress,
    color: Color.White,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
});

export async function buildAssetCardAsCKTable(
  asset: WebAssetResponse,
  attachments: AttachmentResponse[],
  tableTemplate: AssetSelectionTemplate,
  defaultAttachment?: AttachmentResponse
) {
  switch (tableTemplate) {
    case "single-image-with-details":
      return singleImageWithDetails(asset, defaultAttachment || attachments[0]);
    case "single-image":
      return singleImage(asset, defaultAttachment || attachments[0]);
    case "image-grid":
      return imageGrid(attachments, asset);
    case "image-grid-with-details":
      return imageGridWithDetails(attachments, asset);
    default:
      throw new Error(`Invalid ${tableTemplate} template`);
  }
}
